import React, { useEffect, useRef, useState } from "react";
import { Video, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthProvider";
import useSWR from "swr";
import { toast } from "react-toastify";
import { useMeetingStore } from "../../../store/meeting.store";
import RCJMeeting from "../../../components/mediasoup-ui/RCJ.Meeting";

const isDeviceSelectionError = (error: any) =>
  error?.name === "NotFoundError" || error?.name === "OverconstrainedError";

const requestSingleMediaTrack = async (kind: "audio" | "video") => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: kind === "video",
      audio: kind === "audio",
    });
  } catch (error: any) {
    if (isDeviceSelectionError(error)) return null;
    throw error;
  }
};

const requestCameraAndMicPermission = async () => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
  } catch (error: any) {
    if (!isDeviceSelectionError(error)) throw error;

    const videoStream = await requestSingleMediaTrack("video");
    const audioStream = await requestSingleMediaTrack("audio");
    const tracks = [
      ...(videoStream?.getVideoTracks() || []),
      ...(audioStream?.getAudioTracks() || []),
    ];

    if (!tracks.length) throw error;

    return new MediaStream(tracks);
  }
};

const JudgeDashboard: React.FC = () => {
  const { data, isLoading } = useSWR(`/api/judge/cases`);
  const [isSystemReady, setIsSystemReady] = useState(false);
  const { user } = useAuth();
  const meetingStarted = useMeetingStore((s) => s.meetingStarted);
  const setMeetingStarted = useMeetingStore((s) => s.setMeetingStarted);
  const setCauses = useMeetingStore((s) => s.setCauses);
  const setUserData = useMeetingStore((s) => s.setUserData);
  const setCourtRoomData = useMeetingStore((s) => s.setCourtRoomData);
  const [inmeeting, setInMeeting] = useState(false);

  // const handleAllowCameraMic = async () => {
  //   try {
  //     // Check whether this session is still active

  //     setCauses(data?.data);
  //     setCourtRoomData(data?.courtRoomData);
  //     const stream = await navigator.mediaDevices.getUserMedia({
  //       video: true,
  //       audio: true,
  //     });
  //     setInMeeting(true);
  //     if (stream && data?.data?.length > 0) {
  //       setIsSystemReady(stream.active);
  //       setMeetingStarted(true);
  //       requestFullscreen();
  //       setUserData({
  //         _id: user?.id || "",
  //         name: user?.name || "",
  //         role: "Judge",
  //         roomNo: data?.courtRoomData?.courtRoomNo,
  //         roomId: data?.courtRoomData?.courtRoomId,
  //       });
  //     } else if (data?.data?.length === 0) {
  //       toast.error("No cases scheduled for today.");
  //     } else {
  //       toast.error("Please allow camera and microphone access to proceed.");
  //     }
  //   } catch (error: any) {
  //     console.log(error);
  //     toast.error("Error accessing camera and microphone: " + error.message);
  //   } finally {
  //     setInMeeting(false);
  //   }
  // };

  const handleAllowCameraMic = async () => {
    try {
      setInMeeting(true);

      setCauses(data?.data);
      setCourtRoomData(data?.courtRoomData);

      // Request browser permission with generic constraints so Chrome can
      // show the permission popup instead of failing on a stale device id.
      const stream = await requestCameraAndMicPermission();

      if (!stream.active) {
        throw new Error("Camera and microphone stream is not active.");
      }

      const hasCamera = stream.getVideoTracks().length > 0;
      const hasMic = stream.getAudioTracks().length > 0;

      stream.getTracks().forEach((track) => track.stop());

      if (!hasCamera || !hasMic) {
        const error = new Error(
          !hasCamera && !hasMic
            ? "Camera and microphone were not found."
            : !hasCamera
              ? "Camera was not found."
              : "Microphone was not found.",
        );
        error.name = "NotFoundError";
        throw error;
      }

      // Permission granted and devices available
      setIsSystemReady(true);
      setMeetingStarted(true);

      setUserData({
        _id: user?.id || "",
        name: user?.name || "",
        role: "Judge",
        roomNo: data?.courtRoomData?.courtRoomNo,
        roomId: data?.courtRoomData?.courtRoomId,
      });

      requestFullscreen();
      setInMeeting(false);
    } catch (error: any) {
      console.error("Media access error:", error);

      switch (error?.name) {
        case "NotAllowedError":
        case "PermissionDeniedError":
          toast.error(
            "Camera and microphone permission is required. Please allow access in your browser.",
          );
          break;

        case "NotFoundError":
          toast.error(
            "Camera or microphone was not found. Please connect/enable your camera and microphone.",
          );
          break;

        case "NotReadableError":
          toast.error(
            "Camera or microphone is already being used by another application.",
          );
          break;

        case "OverconstrainedError":
          toast.error(
            "The requested camera or microphone configuration is not available.",
          );
          break;

        case "SecurityError":
          toast.error(
            "Camera and microphone access is blocked for security reasons.",
          );
          break;

        default:
          toast.error(
            `Unable to access camera and microphone: ${
              error?.message || "Unknown error"
            }`,
          );
      }

      setIsSystemReady(false);
      setMeetingStarted(false);
      setInMeeting(false);
    }
  };

  const elementRef = useRef<HTMLDivElement>(null);

  const expectedCasesCount = Number(
    data?.pagination?.total ?? data?.data?.length ?? 0,
  );
  const hasCases = expectedCasesCount > 0;
  const isStartDisabled = isLoading || !hasCases;

  const requestFullscreen = () => {
    const el = elementRef.current;
    if (!el) return;

    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if ((el as any).webkitRequestFullscreen) {
      (el as any).webkitRequestFullscreen(); // Safari
    } else if ((el as any).msRequestFullscreen) {
      (el as any).msRequestFullscreen(); // IE11
    }
  };
  useEffect(() => {
    if (meetingStarted && import.meta.env.VITE_APP_ENV === "production") {
      requestFullscreen();
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  }, [meetingStarted]);

  return meetingStarted ? (
    <div ref={elementRef} className="court-fullscreen">
      {/* <JudgeMeeting courtRoomData={data?.courtRoomData} /> */}
      <RCJMeeting />
    </div>
  ) : (
    <div className="min-h-screen  flex items-center justify-center p-2">
      <div className="w-full max-w-xl mx-auto bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 rounded-3xl shadow-xl p-6 sm:p-10">
        <div>
          {/* Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center shadow-lg">
              <Users className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-white text-2xl md:text-3xl font-bold text-center mb-6 leading-tight">
            Judicial Session Portal
          </h1>

          {/* Description */}
          <p className="text-blue-100 text-center mb-8 leading-relaxed px-2">
            Welcome, Your Honor. Click below to initiate the court session and
            connect with all participants via secure video conference.
          </p>

          {/* Information Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <div className="space-y-4">
              {/* Court Number */}
              <div className="flex items-start gap-4">
                <span className="text-blue-200 font-medium shrink-0">
                  Room Number:
                </span>
                <span className="flex-1 min-w-0 text-white  font-bold text-lg  text-right  wrap-break-word whitespace-normal">
                  {data?.courtRoomData?.courtRoomNo}
                </span>
              </div>

              {/* Judge Name */}
              <div className="flex justify-between items-start">
                {data?.courtType == "high_courts" ? (
                  <span className="text-blue-200 font-medium">Bench Name:</span>
                ) : (
                  <span className="text-blue-200 font-medium">Judge Name:</span>
                )}
                <span className="text-white font-bold text-right leading-tight">
                  {data?.courtType === "high_courts"
                    ? data?.courtRoomData?.benchName
                    : `${user?.name}`}
                </span>
              </div>

              {/* Expected Cases */}
              <div className="flex justify-between items-center">
                <span className="text-blue-200 font-medium">
                  Expected Cases:
                </span>
                <span className="text-white font-bold text-lg">
                  {expectedCasesCount}
                </span>
              </div>
            </div>
          </div>

          {/* Start Session Button */}
          <button
            onClick={handleAllowCameraMic}
            disabled={isStartDisabled || inmeeting}
            className={`w-full py-4 px-6 rounded-lg shadow-lg transition-all duration-200 mb-4 focus:outline-none focus:ring-4 focus:ring-white/40
              ${
                !isStartDisabled
                  ? "cursor-pointer bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 text-white hover:from-green-500 hover:via-emerald-600 hover:to-green-700 hover:shadow-2xl hover:shadow-emerald-900/30 transform hover:scale-[1.02] active:scale-[0.98]"
                  : "cursor-not-allowed bg-slate-500/60 text-slate-200 opacity-70"
              }`}
          >
            <div className="flex items-center justify-center space-x-3">
              <Video className="w-6 h-6" />
              <span className="text-lg font-semibold">
                {isLoading ? "Checking Cases..." : "Start Session"}
              </span>
            </div>
          </button>

          {/* System Status */}
          <div className="flex items-center justify-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                isSystemReady ? "bg-green-400" : "bg-red-400"
              } animate-pulse`}
            ></div>
            <span className="text-green-300 text-sm font-medium">
              System Ready
            </span>
          </div>
        </div>

        {/* Additional Mobile Optimizations */}
        <div className="mt-8 text-center">
          <p className="text-blue-300 text-sm opacity-75">
            Secure • Encrypted • Court Authorized
          </p>
        </div>
      </div>
    </div>
  );
};

export default JudgeDashboard;
